import { useMemo } from "react";
import { Box, VStack, useDisclosure } from "@chakra-ui/react";
import CDS from "design-system";
import { useSharedResourcesListByNewHireIdQuery } from "features/Resources/Shared/hooks";
import { Task, TaskSummary } from "features/Resources/Task/models";
import TaskCard from "features/Resources/Task/Components/UserTaskSummary/TaskCard";
import ActionSideDrawer from "components/ActionSideDrawer";
import useActionSideDrawer from "components/ActionSideDrawer/hooks";
import type { NewHireJourney } from "models/v2/newHireJourney";
import { useIssuesByNewHireIdQuery } from "features/Issues/hooks";
import { dedupeMissingSlackUsers } from "features/Issues/helpers";
import { ISSUE_TYPE_LABELS } from "features/Issues/constants";
import IssuesDrawer from "features/Issues/Components/IssuesDrawer";
import OverviewSection from "features/NewHireJourney/Components/Overview/OverviewSection";
import CompactMeetingRow from "features/NewHireJourney/Components/Overview/CompactMeetingRow";
import CompactCheckpointRow from "features/NewHireJourney/Components/Overview/CompactCheckpointRow";
import { getOpenTasksSorted } from "features/Resources/Task/helpers/stats";
import { getUpcomingMeetings } from "features/Resources/Meeting/helpers";
import { getCheckpoints, getNeedsActionItems } from "features/NewHireJourney/Components/Overview/helpers";
import NeedsActionBlock from "features/NewHireJourney/Components/Overview/NeedsActionBlock";

interface OverviewTabProps {
  newHireJourney: NewHireJourney | undefined;
  allTasks: TaskSummary[] | undefined;
}

export default function OverviewTab({ newHireJourney, allTasks }: OverviewTabProps) {
  const journeyId = newHireJourney?.id || "";

  const { data: actions } = useSharedResourcesListByNewHireIdQuery(journeyId, {
    enabled: !!journeyId,
  });

  const { data: issues } = useIssuesByNewHireIdQuery(journeyId, { enabled: !!journeyId });
  const dedupedIssues = useMemo(() => dedupeMissingSlackUsers(issues), [issues]);

  const { isOpen: isIssueDrawerOpen, onOpen: onIssueDrawerOpen, onClose: onIssueDrawerClose } = useDisclosure();

  const { isOpen, onClose, selectedActionPrefixId, expandClickHandlerByPrefixId } = useActionSideDrawer();

  const allOpenTasks = useMemo(() => getOpenTasksSorted(allTasks), [allTasks]);
  const openTasks = useMemo(() => allOpenTasks.slice(0, 5), [allOpenTasks]);
  const allUpcomingMeetings = useMemo(() => getUpcomingMeetings(actions), [actions]);
  const upcomingMeetings = useMemo(() => allUpcomingMeetings.slice(0, 3), [allUpcomingMeetings]);
  const checkpointActions = useMemo(() => getCheckpoints(actions), [actions]);

  const issueCount = dedupedIssues?.length || 0;
  const issueSubtitle = useMemo(() => {
    if (!dedupedIssues || dedupedIssues.length === 0) return "";
    const first = dedupedIssues[0];
    const label = ISSUE_TYPE_LABELS[first.issueType] || first.issueType;
    const name = first.newHireName || "";
    return [label, name].filter(Boolean).join(" · ");
  }, [dedupedIssues]);

  const needsActionItems = useMemo(
    () => getNeedsActionItems(allTasks, actions, journeyId, issueCount, issueSubtitle),
    [allTasks, actions, journeyId, issueCount, issueSubtitle],
  );

  const handleTaskClick = (task: Task | TaskSummary) => {
    const prefixId = task.prefixId;
    if (prefixId) {
      expandClickHandlerByPrefixId(prefixId);
    }
  };

  return (
    <VStack spacing="6" align="stretch">
      <NeedsActionBlock items={needsActionItems} onIssueClick={onIssueDrawerOpen} />

      {/* Tasks */}
      <OverviewSection
        title="Tasks"
        subtitle={
          allOpenTasks.length > 0 ? `Showing ${openTasks.length} of ${allOpenTasks.length} Open Tasks` : undefined
        }
        linkTo={`/new-hires/${journeyId}/tasks`}
      >
        {openTasks.length > 0 ? (
          <Box>
            {openTasks.map((task) => (
              <TaskCard key={task.id} task={task as Task} onClick={handleTaskClick} showAssignee compact />
            ))}
          </Box>
        ) : (
          <CDS.Text12 color="content.tertiary" py="4" textAlign="center">
            No open tasks
          </CDS.Text12>
        )}
      </OverviewSection>

      {/* Upcoming meetings */}
      <OverviewSection
        title="Upcoming meetings"
        subtitle={
          allUpcomingMeetings.length > 0
            ? `Showing ${upcomingMeetings.length} of ${allUpcomingMeetings.length} Meetings`
            : undefined
        }
        linkTo={`/new-hires/${journeyId}/meetings`}
      >
        {upcomingMeetings.length > 0 ? (
          <Box>
            {upcomingMeetings.map((meeting) => (
              <CompactMeetingRow
                key={meeting.prefixId}
                action={meeting}
                onClick={() => expandClickHandlerByPrefixId(meeting.prefixId)}
              />
            ))}
          </Box>
        ) : (
          <CDS.Text12 color="content.tertiary" py="4" textAlign="center">
            No meetings scheduled yet
          </CDS.Text12>
        )}
      </OverviewSection>

      {/* Checkpoints */}
      <OverviewSection
        title="Checkpoints"
        subtitle={
          checkpointActions.length > 0
            ? `${checkpointActions.length} Checkpoint${checkpointActions.length !== 1 ? "s" : ""}`
            : undefined
        }
        linkTo={`/new-hires/${journeyId}/schedule`}
      >
        {checkpointActions.length > 0 ? (
          <Box>
            {checkpointActions.map((checkpoint) => (
              <CompactCheckpointRow key={checkpoint.prefixId} action={checkpoint} />
            ))}
          </Box>
        ) : (
          <CDS.Text12 color="content.tertiary" py="4" textAlign="center">
            No checkpoints
          </CDS.Text12>
        )}
      </OverviewSection>

      <ActionSideDrawer isOpen={isOpen} onClose={onClose} prefixId={selectedActionPrefixId} />
      <IssuesDrawer newHireId={journeyId} isOpen={isIssueDrawerOpen} onClose={onIssueDrawerClose} />
    </VStack>
  );
}
